/**
 * RevenueCat Hook
 *
 * Provides subscription management functionality using RevenueCat SDK
 *
 * ACTIVATION REQUIRED:
 * 1. Install package: npm install @revenuecat/purchases-js
 * 2. Set VITE_REVENUECAT_PUBLIC_KEY in .env
 * 3. Uncomment the code below
 * 4. Configure products in RevenueCat dashboard
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';

// UNCOMMENT WHEN READY TO ACTIVATE:
// import Purchases, { type Package, type CustomerInfo } from '@revenuecat/purchases-js';

interface UseRevenueCatReturn {
  isConfigured: boolean;
  isPremium: boolean;
  isLoading: boolean;
  packages: unknown[]; // Replace with Package[] when activated
  purchase: (pkg: unknown) => Promise<void>; // Replace with Package when activated
  checkEntitlement: (entitlement: string) => Promise<boolean>;
  restorePurchases: () => Promise<void>;
  error: string | null;
}

/**
 * Hook for managing RevenueCat subscriptions
 *
 * @example
 * ```tsx
 * const { isPremium, purchase, packages } = useRevenueCat();
 *
 * if (!isPremium) {
 *   return <PaywallModal packages={packages} onPurchase={purchase} />;
 * }
 * ```
 */
export function useRevenueCat(): UseRevenueCatReturn {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [packages, setPackages] = useState<unknown[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Check if RevenueCat is configured
  const revenueCatKey = import.meta.env.VITE_REVENUECAT_PUBLIC_KEY;
  const isConfigured = Boolean(revenueCatKey);

  useEffect(() => {
    if (!isConfigured) {
      console.warn('⚠️  RevenueCat not configured - subscription features disabled');
      console.warn('   Set VITE_REVENUECAT_PUBLIC_KEY to enable');
      setIsLoading(false);
      return;
    }

    if (!user) {
      setIsLoading(false);
      return;
    }

    // UNCOMMENT WHEN READY TO ACTIVATE:
    /*
    async function initializeRevenueCat() {
      try {
        // Configure RevenueCat
        await Purchases.configure({
          apiKey: revenueCatKey!,
          appUserId: user.id,
        });

        // Load available packages
        const offerings = await Purchases.getOfferings();
        if (offerings.current) {
          setPackages(offerings.current.availablePackages);
        }

        // Check current entitlements
        const customerInfo = await Purchases.getCustomerInfo();
        const hasPremium = customerInfo.entitlements.active['premium'] !== undefined;
        setIsPremium(hasPremium);

        setIsLoading(false);
      } catch (err: unknown) {
        console.error('RevenueCat initialization error:', err);
        setError(err instanceof Error ? err.message : 'Failed to initialize RevenueCat');
        setIsLoading(false);
      }
    }

    initializeRevenueCat();
    */

    // TEMPORARY: Mock response until activated
    console.log('RevenueCat configured but not activated - awaiting implementation');
    setIsLoading(false);
  }, [user, revenueCatKey, isConfigured]);

  /**
   * Purchase a subscription package
   */
  const purchase = async (pkg: unknown) => {
    if (!isConfigured) {
      throw new Error('RevenueCat not configured');
    }

    setIsLoading(true);
    setError(null);

    try {
      // UNCOMMENT WHEN READY TO ACTIVATE:
      /*
      const { customerInfo } = await Purchases.purchasePackage(pkg as Package);
      const hasPremium = customerInfo.entitlements.active['premium'] !== undefined;
      setIsPremium(hasPremium);
      */

      // TEMPORARY: Mock success until activated
      console.log('Purchase attempted (mock):', pkg);
      throw new Error('RevenueCat not fully activated - purchases disabled');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Purchase failed';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Check if user has a specific entitlement
   */
  const checkEntitlement = async (entitlement: string): Promise<boolean> => {
    if (!isConfigured) {
      // When not configured, grant all features (free tier)
      return true;
    }

    try {
      // UNCOMMENT WHEN READY TO ACTIVATE:
      /*
      const customerInfo = await Purchases.getCustomerInfo();
      return customerInfo.entitlements.active[entitlement] !== undefined;
      */

      // TEMPORARY: Return true (all features free) until activated
      console.log(`Entitlement check (mock): ${entitlement} = true`);
      return true;
    } catch (err: unknown) {
      console.error('Entitlement check error:', err);
      // Fail open - grant access if check fails
      return true;
    }
  };

  /**
   * Restore previous purchases
   */
  const restorePurchases = async () => {
    if (!isConfigured) {
      throw new Error('RevenueCat not configured');
    }

    setIsLoading(true);
    setError(null);

    try {
      // UNCOMMENT WHEN READY TO ACTIVATE:
      /*
      const customerInfo = await Purchases.restorePurchases();
      const hasPremium = customerInfo.entitlements.active['premium'] !== undefined;
      setIsPremium(hasPremium);
      */

      // TEMPORARY: Mock success until activated
      console.log('Restore purchases attempted (mock)');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Restore failed';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isConfigured,
    isPremium,
    isLoading,
    packages,
    purchase,
    checkEntitlement,
    restorePurchases,
    error,
  };
}
